package components.accounting

import components.editorNameForText
import components.userName
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.lizergy.model.company.UserResolver
import it.neckar.lizergy.model.financial.currency.Money
import it.neckar.lizergy.model.income.IncomePercentage
import it.neckar.lizergy.model.income.IncomePercentageCategory
import it.neckar.lizergy.model.price.ResolvedCompanyEarningsEntry
import it.neckar.lizergy.model.price.ResolvedEarningsEntry
import it.neckar.lizergy.model.price.ResolvedNeckarITEarningsEntry
import it.neckar.lizergy.model.project.previews.AccountingProjectPreview
import it.neckar.lizergy.model.project.previews.AccountingResolvedConfigurationPreview
import it.neckar.lizergy.model.project.previews.AccountingResolvedProjectPreview
import it.neckar.open.collections.fastForEach
import it.neckar.open.formatting.dateFormat
import it.neckar.open.formatting.dateTimeFormat
import it.neckar.open.formatting.format
import it.neckar.open.formatting.formatAsPercentage
import it.neckar.open.kotlin.lang.percent
import it.neckar.react.common.*
import it.neckar.react.common.FontAwesome.faCircleCheck
import it.neckar.react.common.form.*
import it.neckar.react.common.table.*
import kotlinx.html.DIV
import kotlinx.html.InputType
import kotlinx.html.title
import plannerI18nConfiguration
import react.*
import react.dom.*
import store.hooks.useSelectCompanyResolver
import store.hooks.useSelectUserResolver

fun accountingTable(howDoYouLikeYourQuoteElements: AccountingProjectPreview.QuoteElements): TableDataColumn<AccountingResolvedProjectPreview> {
  return TableDataColumn(
    id = "accountingTable",
    title = "Abrechnung",
    titleClasses = "justify-content-between col-lg-4",
    sortFunction = compareBy { it.currentConfigurationPreview?.getNetPrice(howDoYouLikeYourQuoteElements) },
  ) { projectPreview ->
    {
      val currentConfigurationPreview = projectPreview.currentConfigurationPreview
      if (currentConfigurationPreview != null) {
        AccountingTable {
          attrs {
            this.projectPreview = projectPreview
            this.currentConfigurationPreview = currentConfigurationPreview
            this.howDoYouLikeYourQuoteElements = howDoYouLikeYourQuoteElements
          }
        }
      } else {
        p("form-text") {
          +"Kein Angebot vorhanden"
        }
      }
    }
  }
}

val AccountingTable: FC<AccountingTableProps> = fc("AccountingTable") { props ->
  val userResolver = useSelectUserResolver()
  val companyResolver = useSelectCompanyResolver()

  val projectPreview = props::projectPreview.safeGet()
  val currentConfigurationPreview = props::currentConfigurationPreview.safeGet()
  val howDoYouLikeYourQuoteElements = props::howDoYouLikeYourQuoteElements.safeGet()

  val earningsDistribution = currentConfigurationPreview.earningsDistribution
  val upToDateQuoteElements = currentConfigurationPreview.getQuoteElements(howDoYouLikeYourQuoteElements)
  val manualQuoteElements = currentConfigurationPreview.manualQuoteElements

  val accountingTableEntriesList = buildList {
    add(EarningsCompanyEntryAndInfo(earningsDistribution.tippgeber, earningsDistribution.tippgeber.manualEarnings?.euros, earningsDistribution.tippgeber.invoiceHistory.currentValue?.value))
    add(EarningsCompanyEntryAndInfo(earningsDistribution.vertriebProjekterfassung, earningsDistribution.vertriebProjekterfassung.manualEarnings?.euros, earningsDistribution.vertriebProjekterfassung.invoiceHistory.currentValue?.value))
    add(EarningsCompanyEntryAndInfo(earningsDistribution.vertriebAngebotsvorstellung, earningsDistribution.vertriebAngebotsvorstellung.manualEarnings?.euros, earningsDistribution.vertriebAngebotsvorstellung.invoiceHistory.currentValue?.value))
    add(EarningsCompanyEntryAndInfo(earningsDistribution.technischePlanungDach, earningsDistribution.technischePlanungDach.manualEarnings?.euros, earningsDistribution.technischePlanungDach.invoiceHistory.currentValue?.value))
    add(EarningsCompanyEntryAndInfo(earningsDistribution.technischePlanungElektrik, earningsDistribution.technischePlanungElektrik.manualEarnings?.euros, earningsDistribution.technischePlanungElektrik.invoiceHistory.currentValue?.value))
    add(EarningsCompanyEntryAndInfo(earningsDistribution.montageDach, earningsDistribution.montageDach.manualEarnings?.euros, earningsDistribution.montageDach.invoiceHistory.currentValue?.value))
    add(EarningsCompanyEntryAndInfo(earningsDistribution.montageGeruest, earningsDistribution.montageGeruest.manualEarnings?.euros, earningsDistribution.montageGeruest.invoiceHistory.currentValue?.value))
    add(EarningsCompanyEntryAndInfo(earningsDistribution.elektroInstallation, earningsDistribution.elektroInstallation.manualEarnings?.euros, earningsDistribution.elektroInstallation.invoiceHistory.currentValue?.value))
    add(EarningsCompanyEntryAndInfo(earningsDistribution.netzvoranfrage, earningsDistribution.netzvoranfrage.manualEarnings?.euros, earningsDistribution.netzvoranfrage.invoiceHistory.currentValue?.value))
    add(EarningsCompanyEntryAndInfo(earningsDistribution.neckarITAccountingStatus, earningsDistribution.neckarITAccountingStatus.manualEarnings?.euros, earningsDistribution.neckarITAccountingStatus.invoiceHistory.currentValue?.value))
  }.groupBy { it.earningsCompanyEntry.companyProfile }.map {
    AccountingTableEntries(
      companyProfile = it.key,
      configurationPreview = currentConfigurationPreview,
      earningsCompanyEntries = it.value,
    )
  }.sortedByDescending { it.companyProfile }


  accountingTableEntriesList.fastForEach { accountingTableEntries ->
    val companyInformation = companyResolver[accountingTableEntries.companyProfile]
    val earningsCompanyEntries = accountingTableEntries.relevantEarningsCompanyEntries
    val openEarningsCompanyEntries = accountingTableEntries.openEarningsCompanyEntries
    val accountedEarningsCompanyEntries = earningsCompanyEntries.size - openEarningsCompanyEntries.size

    div("mb-3") {
      div("row") {
        div("col") {
          b { +companyInformation.name }
          span("ms-2") {
            attrs {
              addClass("text-center")
              addClass("align-middle")

              if (openEarningsCompanyEntries.isEmpty()) {
                addClass("text-success")
              } else if (accountedEarningsCompanyEntries > 0) {
                addClass("text-warning")
              } else {
                addClass("text-danger")
              }
            }

            if (openEarningsCompanyEntries.isEmpty()) {
              faCircleCheck()
            } else {
              b { +"${accountedEarningsCompanyEntries}/${earningsCompanyEntries.size}" }
            }
          }
        }
        div("col text-end") {
          +accountingTableEntries.relevantEarnings(howDoYouLikeYourQuoteElements).format(plannerI18nConfiguration)
        }
      }

      earningsCompanyEntries.fastForEach { earningsCompanyEntryAndState ->
        val earningsCompanyEntry = earningsCompanyEntryAndState.earningsCompanyEntry
        val manualIncome = earningsCompanyEntryAndState.manualIncome
        val invoiceNumber = earningsCompanyEntryAndState.invoiceNumber
        val incomePercentage = when (earningsCompanyEntry) {
          is ResolvedCompanyEarningsEntry -> earningsCompanyEntry.incomePercentage
          is ResolvedNeckarITEarningsEntry -> currentConfigurationPreview.softwareIncomePercentage
        }

        earningsInputField(
          upToDateConfigurationPreview = currentConfigurationPreview,
          incomePercentage = incomePercentage,
          howDoYouLikeYourQuoteElements = howDoYouLikeYourQuoteElements,
          projectPreview = projectPreview,
          earningsCompanyEntry = earningsCompanyEntry,
          invoiceNumber = invoiceNumber,
          manualIncome = manualIncome,
          userResolver = userResolver,
        )
      }
    }

    hr {}
  }

  IncomePercentageCategory.entries.fastForEach { incomePercentageCategory ->
    val income = manualQuoteElements.manualSumsForTags[incomePercentageCategory]?.currentValue
    val netPriceForCategory = upToDateQuoteElements.sumsForTags[incomePercentageCategory]

    div("row") {
      div("col form-text my-auto") { +incomePercentageCategory.description }
      div("col-5 text-end") {
        val id = UniqueId.create("${projectPreview.uuid}-${incomePercentageCategory.name}")
        euroInputField(value = income?.euros, fieldName = id, netPriceForCategory = netPriceForCategory)
        manualQuoteElements.manualSumsForTags[incomePercentageCategory]?.let { editHistoryEntry ->
          div("form-text") {
            userName(editHistoryEntry.currentEdit.loginName, userResolver)
            +": "
            +dateFormat.format(editHistoryEntry.lastEditedTime, plannerI18nConfiguration)
          }
        }
      }
    }
  }

  hr {}

  if (upToDateQuoteElements != null && upToDateQuoteElements.discountPercentage != 0.0.percent) {
    div("row") {
      div("col my-auto") { b { +"Rechnungssumme" } }
      div("col text-end") { b { +currentConfigurationPreview.getTotalPrice(howDoYouLikeYourQuoteElements).format(plannerI18nConfiguration) } }
    }
    div("row") {
      div("col my-auto") { +"Rabatt (${upToDateQuoteElements.discountPercentage.formatAsPercentage(0)})" }
      div("col text-end") { +currentConfigurationPreview.getDiscount(howDoYouLikeYourQuoteElements).format(plannerI18nConfiguration) }
    }
  }
  div("row") {
    div("col my-auto") { b { +"Nettosumme" } }
    div("col text-end") { b { +currentConfigurationPreview.getNetPrice(howDoYouLikeYourQuoteElements).format(plannerI18nConfiguration) } }
  }
}

data class EarningsCompanyEntryAndInfo(
  val earningsCompanyEntry: ResolvedEarningsEntry,
  val manualIncome: Double?,
  val invoiceNumber: String?,
)

private fun RDOMBuilder<DIV>.earningsInputField(
  upToDateConfigurationPreview: AccountingResolvedConfigurationPreview,
  incomePercentage: IncomePercentage,
  howDoYouLikeYourQuoteElements: AccountingProjectPreview.QuoteElements,
  projectPreview: AccountingResolvedProjectPreview,
  earningsCompanyEntry: ResolvedEarningsEntry,
  invoiceNumber: String?,
  manualIncome: Double?,
  userResolver: UserResolver,
) {
  EarningsInputField {
    attrs {
      this.upToDateConfigurationPreview = upToDateConfigurationPreview
      this.incomePercentage = incomePercentage
      this.howDoYouLikeYourQuoteElements = howDoYouLikeYourQuoteElements
      this.projectPreview = projectPreview
      this.earningsCompanyEntry = earningsCompanyEntry
      this.invoiceNumber = invoiceNumber
      this.manualIncome = manualIncome
      this.userResolver = userResolver
    }
  }
}

private val EarningsInputField: FC<EarningsInputFieldProps> = fc("EarningsInputField") { props ->
  val upToDateConfigurationPreview = props::upToDateConfigurationPreview.safeGet()
  val incomePercentage = props::incomePercentage.safeGet()
  val howDoYouLikeYourQuoteElements = props::howDoYouLikeYourQuoteElements.safeGet()
  val projectPreview = props::projectPreview.safeGet()
  val earningsCompanyEntry = props::earningsCompanyEntry.safeGet()
  val invoiceNumber = props::invoiceNumber.safeGet()
  val manualIncome = props::manualIncome.safeGet()
  val userResolver = props::userResolver.safeGet()


  div("row") {
    div("col my-auto") {
      span {
        attrs {
          addClass("text-center")
          addClass("align-middle")
          addClass("text-success")
        }

        +(invoiceNumber ?: "-")
      }
      span("form-text ms-3") {
        val currentEdit = earningsCompanyEntry.invoiceHistory.currentEdit
        attrs {
          this.title = buildString {
            earningsCompanyEntry.invoiceHistory.allEdits.fastForEach {
              val invoiceNumber = it.value?.value ?: "-"
              val editor = it.loginName?.let { userResolver[it].editorNameForText() } ?: "-"
              val editTime = dateFormat.format(it.editTime, plannerI18nConfiguration)
              appendLine("$editTime: $editor")
              appendLine(invoiceNumber)
              appendLine()
            }
          }
        }
        userName(currentEdit.loginName, userResolver)
        +": "
        if (currentEdit.value != null) {
          +dateTimeFormat.format(earningsCompanyEntry.invoiceHistory.lastEditedTime, plannerI18nConfiguration)
        } else {
          +dateFormat.format(earningsCompanyEntry.invoiceHistory.lastEditedTime, plannerI18nConfiguration)
        }
      }
    }

    val relevantEarnings = upToDateConfigurationPreview.getEarningsForIncomePercentage(incomePercentage, howDoYouLikeYourQuoteElements)
    div("col-5 text-end") {
      val id = UniqueId.create("${projectPreview.uuid}-${earningsCompanyEntry.company}-${incomePercentage.description}")
      euroInputField(value = manualIncome, fieldName = id, netPriceForCategory = relevantEarnings)
      div("form-text") {
        userName(earningsCompanyEntry.manualEarningsHistory.currentEdit.loginName, userResolver)
        +": "
        +dateFormat.format(earningsCompanyEntry.manualEarningsHistory.lastEditedTime, plannerI18nConfiguration)
      }
    }
  }
}

private fun RDOMBuilder<DIV>.euroInputField(
  value: Double?,
  fieldName: String,
  netPriceForCategory: Money?,
  mergeAbove: Boolean = false,
  mergeBelow: Boolean = false,
) {
  div("input-group") {
    nullableInputField(
      value = value?.toString(),
      onChange = { },
      fieldName = fieldName,
      title = netPriceForCategory?.format(plannerI18nConfiguration) ?: "Nettopreis",
      placeholder = (netPriceForCategory?.euros ?: 0.00).format(i18nConfiguration = plannerI18nConfiguration),
      classes = "form-control float-end text-end",
      editableStatus = EditableStatus.ReadOnly,
    ) {
      attrs {
        type = InputType.number
        if (mergeAbove) mergedAbove()
        if (mergeBelow) mergedBelow()
      }
    }
    div("input-group-append") {
      span("input-group-text") { +"€" }
    }
  }
}


external interface EarningsInputFieldProps : Props {
  var upToDateConfigurationPreview: AccountingResolvedConfigurationPreview
  var incomePercentage: IncomePercentage
  var howDoYouLikeYourQuoteElements: AccountingProjectPreview.QuoteElements
  var projectPreview: AccountingResolvedProjectPreview
  var earningsCompanyEntry: ResolvedEarningsEntry
  var invoiceNumber: String?
  var manualIncome: Double?
  var userResolver: UserResolver
}

external interface AccountingTableProps : Props {
  var projectPreview: AccountingResolvedProjectPreview
  var currentConfigurationPreview: AccountingResolvedConfigurationPreview
  var howDoYouLikeYourQuoteElements: AccountingProjectPreview.QuoteElements
}
